import { Component, Inject } from '@angular/core';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../_services/notification.service';
import {
  ComunicacionDetalle,
  DestinatarioDetalle,
  DestinatarioMin,
  DocumentoRef,
  TIPO_ENVIO_COMUNICACION_ID,
  TIPO_ENVIO_NOTIFICACION_ID,
  etiquetaDestinatario,
  inicialesDestinatario
} from '../comunicacion.model';
import { GestionRemesasService } from '../gestion-remesas.service';

export interface RemesaDetailData {
  comunicacion: ComunicacionDetalle;
}

export type RemesaDetailAction = 'edit' | 'duplicate' | 'delete' | 'firmar-enviar';

@Component({
  selector: 'app-remesa-detail-drawer',
  templateUrl: './remesa-detail-drawer.component.html',
  styleUrls: ['./remesa-detail-drawer.component.scss']
})
export class RemesaDetailDrawerComponent {

  /** idGestor del documento que se está descargando (deshabilita su botón). */
  descargando: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<RemesaDetailDrawerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RemesaDetailData,
    private service: GestionRemesasService,
    private notif: NotificationService,
    private translate: TranslateService
  ) {}

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  cancelar(): void { this.dialogRef.close(null); }

  descargar(doc: DocumentoRef | undefined, nombrePorDefecto: string): void {
    if (!doc?.idGestor || this.descargando) return;
    this.descargando = doc.idGestor;
    this.service.downloadB64(doc.idGestor).subscribe({
      next: resp => {
        const link = document.createElement('a');
        link.href = `data:${doc.mime || 'application/pdf'};base64,${resp.Result}`;
        link.download = doc.nombre || nombrePorDefecto;
        link.click();
        this.descargando = null;
      },
      error: () => {
        this.descargando = null;
        this.notif.error(this.t('COMMON.ERROR'), this.t('REMESAS.DETAIL.ERROR_DESCARGA'));
      }
    });
  }

  estadoEnvioLabel(d: DestinatarioDetalle): string {
    if (d.estadoEnvioLabel) return d.estadoEnvioLabel;
    switch (d.estadoEnvio) {
      case 'ACEPTADA': return this.t('REMESAS.ESTADO_ENVIO.ACEPTADA');
      case 'RECHAZADA': return this.t('REMESAS.ESTADO_ENVIO.RECHAZADA');
      case 'RECHAZADA AUTOMATICAMENTE': return this.t('REMESAS.ESTADO_ENVIO.RECHAZADA_AUTO');
      case 'PENDIENTE': return this.t('REMESAS.ESTADO_ENVIO.PENDIENTE');
      case 'PENDIENTE DE ENVIO': return this.t('REMESAS.ESTADO_ENVIO.PENDIENTE_ENVIO');
      default: return d.estadoEnvio || '';
    }
  }

  emit(action: RemesaDetailAction): void { this.dialogRef.close(action); }

  iniciales(d: DestinatarioMin): string {
    return inicialesDestinatario(d);
  }

  etiquetaDest(d: DestinatarioMin): string {
    return etiquetaDestinatario(d);
  }

  formatFecha(value: string | number[] | undefined): string {
    if (!value) return '';
    let date: Date;
    if (Array.isArray(value)) {
      const [y, m, d, h, mi] = value;
      date = new Date(y, (m || 1) - 1, d || 1, h || 0, mi || 0);
    } else {
      date = new Date(value);
    }
    if (isNaN(date.getTime())) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${date.getFullYear()}`;
  }

  isBorrador(): boolean { return this.data.comunicacion.estado === 'BORRADOR'; }
  isEnviada(): boolean { return this.data.comunicacion.estado === 'ENVIADA'; }
  isProgramada(): boolean { return this.data.comunicacion.estado === 'PROGRAMADA'; }
  isPendienteFirma(): boolean { return this.data.comunicacion.estado === 'PENDIENTE_FIRMA'; }

  nombreUsuario(u?: { nombre?: string; apellido1?: string; apellido2?: string } | null): string {
    if (!u) return '';
    return [u.nombre, u.apellido1, u.apellido2].filter(Boolean).join(' ').trim();
  }

  estadoLabel(): string {
    switch (this.data.comunicacion.estado) {
      case 'ENVIADA': return this.t('REMESAS.ESTADO.ENVIADA');
      case 'PROGRAMADA': return this.t('REMESAS.ESTADO.PROGRAMADA');
      case 'PENDIENTE_FIRMA': return this.t('REMESAS.ESTADO.PENDIENTE_FIRMA');
      default: return this.t('REMESAS.ESTADO.BORRADOR');
    }
  }

  tipologiaLabel(): string {
    const id = this.data.comunicacion.tipoEnvio?.id;
    return this.t(id === TIPO_ENVIO_NOTIFICACION_ID
      ? 'REMESAS.TIPOLOGIA.NOTIFICACION'
      : 'REMESAS.TIPOLOGIA.COMUNICACION');
  }

  esComunicacion(): boolean {
    return this.data.comunicacion.tipoEnvio?.id === TIPO_ENVIO_COMUNICACION_ID;
  }

  esNotificacion(): boolean {
    return this.data.comunicacion.tipoEnvio?.id === TIPO_ENVIO_NOTIFICACION_ID;
  }
}
